import { BaseForm, PasswordField, TextField } from '@itz/react-cms-element';
import { Col, Row } from 'antd';
import React, { useEffect, useState } from 'react';

import CropImageField from '@components/CropImageField';
import { AppConstants, STATUS_ACTIVE, UploadFileTypes } from '@constants';
import useBasicForm from '@hooks/useBasicForm';
import { showErrorMessage } from '@services/notifyService';
import Utils from '@utils';

// Port từ ra-log-monitor-cms/source/src/compoments/user/AdminForm.js (class BasicForm -> hook).
// ⚠️ KHÁC license: nguồn KHÔNG có dropdown 'Quyền' (groupId) — chỉ 7 trường.
// Giữ nguyên: danh sách trường, nhãn, thứ tự, quy tắc required theo chế độ tạo/sửa.
//
// 🚨 KHÔNG port nguyên `validateToConfirmPassword` của nguồn — nó gọi
//    `validateFields(['confirmPassword'], { force: true })` NGAY TRONG validator; ở antd 5 việc này
//    huỷ lượt validate đang chạy và làm `onFinishFailed` trả errorFields RỖNG (bấm Lưu im lặng).
//    Thay bằng: khi giá trị `password` đổi thì validate lại `confirmPassword` từ `onValuesChange`
//    (ngoài validator) — cùng hiệu ứng quan sát được, không dính bẫy.
const AdminForm = ({ formId, onSubmit, isEditing, dataDetail = {}, loadingSave, uploadFile }) => {
    const { form, mixinFuncs, onValuesChange } = useBasicForm({ onSubmit, setIsChangedFormValues: () => {} });

    // nguồn: state.logo = dataDetail.avatar ? `${contentRootUrl}/${avatar}` : ''
    // ⚠️ nguồn có dấu '/' thừa ở đây (cột trong bảng thì KHÔNG có) — giữ nguyên.
    const [logo, setLogo] = useState(dataDetail.avatar ? `${AppConstants.contentRootUrl}/${dataDetail.avatar}` : '');
    const [uploading, setUploading] = useState(false);


    useEffect(() => {
        // nguồn getInitialFormValues(): tạo mới -> { status: STATUS_ACTIVE }; sửa -> dataDetail
        if (isEditing) {
            form.setFieldsValue({ ...dataDetail });
        } else {
            form.resetFields();
            form.setFieldsValue({ status: STATUS_ACTIVE });
        }
        // nguồn componentDidMount: setFieldValue('avatar', dataDetail.avatar)
        form.setFieldValue('avatar', dataDetail.avatar);
        setLogo(dataDetail.avatar ? `${AppConstants.contentRootUrl}/${dataDetail.avatar}` : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataDetail, isEditing]);

    // nguồn handleChangeLogo
    const handleChangeLogo = (info) => {
        if (info.file.status === 'done') {
            Utils.getBase64(info.file.originFileObj, (base64) => setLogo(base64));
        }
    };

    // nguồn uploadFileLogo
    const uploadFileLogo = (file, onSuccess) => {
        setUploading(true);
        uploadFile({
            params: { file, type: UploadFileTypes.AVATAR },
            onCompleted: (result) => {
                form.setFieldValue('avatar', result?.data?.filePath);
                setUploading(false);
                onSuccess();
            },
            onError: (err) => {
                if (err && err.message) {
                    showErrorMessage(err.message);
                    setUploading(false);
                }
            },
        });
    };

    // nguồn compareToPassword
    const compareToPassword = (rule, newPassword) => {
        const password = form.getFieldValue('password');
        if ((password || newPassword) && password !== newPassword) {
            return Promise.reject(new Error('Mật khẩu bạn nhập không khớp!'));
        }
        return Promise.resolve();
    };

    const handleValuesChange = (changedValues, allValues) => {
        // Thay cho validateToConfirmPassword của nguồn (xem ghi chú đầu file).
        if ('password' in changedValues && form.isFieldTouched('confirmPassword')) {
            form.validateFields(['confirmPassword']);
        }
        onValuesChange?.(changedValues, allValues);
    };

    return (
        <BaseForm
            formId={formId}
            form={form}
            onFinish={mixinFuncs.handleSubmit}
            onValuesChange={handleValuesChange}
            onFinishFailed={({ errorFields }) => console.warn('Không submit được:', errorFields)}
            layout="vertical"
            style={{ width: '100%' }}
        >
            <Row gutter={16}>
                <Col span={12}>
                    <CropImageField
                        name="avatar"
                        label="Ảnh đại diện"
                        imageUrl={logo}
                        loading={uploading}
                        disabled={loadingSave}
                        onChange={handleChangeLogo}
                        uploadFile={uploadFileLogo}
                        formItemProps={{ className: 'admin-avatar-field' }}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <TextField
                        name="username"
                        label="Tên đăng nhập"
                        placeholder="Vui lòng nhập tên đăng nhập"
                        disabled={isEditing || loadingSave}
                        required={!isEditing}
                        requiredMsg="Vui lòng nhập tên đăng nhập"
                        // ⚠️ nguồn truyền min={6} — đó là THUỘC TÍNH HTML của <input>, KHÔNG phải
                        //    rule validate (getTextFieldRules chỉ đọc `minLength`). Không thêm rule min.
                        rules={[{ validator: Utils.validateUsernameForm }]}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        name="fullName"
                        label="Họ và tên"
                        placeholder="Vui lòng nhập họ và tên"
                        required
                        requiredMsg="Vui lòng nhập họ và tên"
                        disabled={loadingSave}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <PasswordField
                        name="password"
                        label={isEditing ? 'Mật khẩu mới' : 'Mật khẩu'}
                        placeholder={isEditing ? '' : 'Vui lòng nhập mật khẩu'}
                        required={!isEditing}
                        requiredMsg="Vui lòng nhập mật khẩu"
                        disabled={loadingSave}
                        // nguồn minLength={6} -> i18n textField:minLengthMsg
                        rules={[{ min: 6, message: 'Số ký tự không thể ít hơn 6' }]}
                    />
                </Col>
                <Col span={12}>
                    <PasswordField
                        name="confirmPassword"
                        label={isEditing ? 'Xác nhận mật khẩu mới' : 'Xác nhận mật khẩu'}
                        placeholder={isEditing ? '' : 'Vui lòng nhập xác nhận mật khẩu'}
                        required={!isEditing}
                        requiredMsg="Vui lòng nhập xác nhận mật khẩu"
                        disabled={loadingSave}
                        rules={[{ validator: compareToPassword }]}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <TextField
                        name="email"
                        label="E-mail"
                        type="email"
                        disabled={loadingSave}
                        // nguồn: type="email" -> rule {type:'email'} với i18n textField:invalidEmailMsg
                        rules={[{ type: 'email', message: 'Định dạng email không hợp lệ' }]}
                    />
                </Col>
                <Col span={12}>
                    <TextField
                        name="phone"
                        label="Số điện thoại"
                        placeholder="Vui lòng nhập số điện thoại"
                        required
                        requiredMsg="Vui lòng nhập số điện thoại"
                        disabled={loadingSave}
                        fieldProps={{ type: 'number' }}
                        rules={[{ min: 10, message: 'Số ký tự không thể ít hơn 10' }]}
                    />
                </Col>
            </Row>
        </BaseForm>
    );
};

export default AdminForm;
